import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import {
  Users, UserPlus, UserMinus, Flame, ThermometerSimple, Snowflake,
  CurrencyInr, TrendUp, Handshake, Trophy, FileXls,
} from "@phosphor-icons/react";

const PRESETS = [
  { value: "", label: "All time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This week" },
  { value: "this_month", label: "This month" },
  { value: "custom", label: "Custom" },
];

const KPI_CONFIG = [
  { key: "total_leads",        label: "Total Leads",       icon: Users,             tone: "blue", money: false },
  { key: "new_leads",          label: "New Leads",         icon: UserPlus,          tone: "blue", money: false },
  { key: "assigned_leads",     label: "Assigned",          icon: UserPlus,          tone: "emerald", money: false },
  { key: "unassigned_leads",   label: "Unassigned",        icon: UserMinus,         tone: "amber", money: false },
  { key: "hot_leads",          label: "Hot Leads",         icon: Flame,             tone: "rose", money: false },
  { key: "warm_leads",         label: "Warm Leads",        icon: ThermometerSimple, tone: "amber", money: false },
  { key: "cold_leads",         label: "Cold Leads",        icon: Snowflake,         tone: "blue", money: false },
  { key: "closed_won_amount",  label: "Closed Won",        icon: Trophy,            tone: "emerald", money: true },
  { key: "closed_lost_amount", label: "Closed Lost",       icon: CurrencyInr,       tone: "rose", money: true },
  { key: "pending_deals_amount", label: "Pending Deals",   icon: Handshake,         tone: "indigo", money: true },
  { key: "expected_revenue",   label: "Expected Revenue",  icon: TrendUp,           tone: "indigo", money: true },
];

const TONE_CLASSES = {
  blue:    { bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-200" },
  amber:   { bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  rose:    { bg: "bg-rose-50",    text: "text-rose-700",    border: "border-rose-200" },
  indigo:  { bg: "bg-indigo-50",  text: "text-indigo-700",  border: "border-indigo-200" },
  slate:   { bg: "bg-slate-50",   text: "text-slate-700",   border: "border-slate-200" },
};

const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const ManagerDashboard = ({ user, logout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    range: "this_month",
    from_date: "",
    to_date: "",
    bde_user_id: "",
    bdm_user_id: "",
    source: "",
    status: "",
    city: "",
    customer_type: "",
  });

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/dashboard/manager?${queryString}`);
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load Manager dashboard");
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => { load(); }, [load]);

  const exportXlsx = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/manager/export-xlsx?${queryString}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `manager-dashboard-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel exported");
    } catch (e) {
      toast.error("Failed to export Excel");
    }
  }, [queryString]);

  const setF = (k, v) => setFilters({ ...filters, [k]: v });
  const reset = () => setFilters({
    range: "this_month", from_date: "", to_date: "",
    bde_user_id: "", bdm_user_id: "", source: "", status: "", city: "", customer_type: "",
  });

  const opts = data?.filter_options || { sources: [], cities: [], customer_types: [], statuses: [], users: [] };
  const bdeUsers = (opts.users || []).filter(u => u.role === "bde");
  const bdmUsers = (opts.users || []).filter(u => u.role === "bdm");

  const inputCls = "rounded-none bg-white border border-slate-300 focus:border-[#002FA7] p-2 outline-none text-sm";
  const labelCls = "text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1";

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="p-8">
          <div className="flex justify-between items-start mb-6 flex-wrap gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-1">Team Performance</div>
              <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>
                Manager Dashboard
              </h1>
              <p className="text-base leading-relaxed text-slate-700">Live KPIs, team breakdown, source &amp; campaign attribution — all from your database</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                data-testid="manager-export-btn"
                onClick={exportXlsx}
                className="rounded-none bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 px-5 py-2.5 font-medium transition-colors inline-flex items-center gap-2"
              >
                <FileXls size={16} weight="bold" />
                Export to Excel
              </button>
              <button
                data-testid="manager-refresh-btn"
                onClick={load}
                className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2.5 font-medium transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Filters */}
          <div data-testid="manager-filters" className="bg-white border border-slate-200 p-5 mb-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div>
              <label className={labelCls}>Range</label>
              <select data-testid="filter-range" value={filters.range} onChange={(e) => setF("range", e.target.value)} className={inputCls}>
                {PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            {filters.range === "custom" && (
              <>
                <div>
                  <label className={labelCls}>From</label>
                  <input data-testid="filter-from" type="date" value={filters.from_date} onChange={(e) => setF("from_date", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>To</label>
                  <input data-testid="filter-to" type="date" value={filters.to_date} onChange={(e) => setF("to_date", e.target.value)} className={inputCls} />
                </div>
              </>
            )}
            <div>
              <label className={labelCls}>BDE</label>
              <select data-testid="filter-bde" value={filters.bde_user_id} onChange={(e) => setF("bde_user_id", e.target.value)} className={inputCls}>
                <option value="">All BDEs</option>
                {bdeUsers.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>BDM</label>
              <select data-testid="filter-bdm" value={filters.bdm_user_id} onChange={(e) => setF("bdm_user_id", e.target.value)} className={inputCls}>
                <option value="">All BDMs</option>
                {bdmUsers.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Source</label>
              <select data-testid="filter-source" value={filters.source} onChange={(e) => setF("source", e.target.value)} className={inputCls}>
                <option value="">All sources</option>
                {(opts.sources || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select data-testid="filter-status" value={filters.status} onChange={(e) => setF("status", e.target.value)} className={inputCls}>
                <option value="">All statuses</option>
                {(opts.statuses || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>City</label>
              <select data-testid="filter-city" value={filters.city} onChange={(e) => setF("city", e.target.value)} className={inputCls}>
                <option value="">All cities</option>
                {(opts.cities || []).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Customer Type</label>
              <select data-testid="filter-customer-type" value={filters.customer_type} onChange={(e) => setF("customer_type", e.target.value)} className={inputCls}>
                <option value="">All types</option>
                {(opts.customer_types || []).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button data-testid="filter-reset" onClick={reset} className="w-full rounded-none border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2 text-sm font-medium transition-colors">
                Reset
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="loading-spinner"></div></div>
          ) : !data ? (
            <div className="text-slate-500">No data available.</div>
          ) : (
            <>
              {/* KPI Grid */}
              <div data-testid="manager-kpi-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-10">
                {KPI_CONFIG.map(k => {
                  const Icon = k.icon;
                  const tone = TONE_CLASSES[k.tone] || TONE_CLASSES.slate;
                  const value = data.kpis?.[k.key] ?? 0;
                  return (
                    <div
                      key={k.key}
                      data-testid={`manager-kpi-${k.key}`}
                      className={`bg-white border ${tone.border} p-5 rounded-none shadow-sm hover:shadow-md transition-shadow`}
                    >
                      <div className={`w-9 h-9 ${tone.bg} flex items-center justify-center mb-3`}>
                        <Icon size={18} weight="bold" className={tone.text} />
                      </div>
                      <div className="text-2xl lg:text-3xl font-bold text-slate-900 tabular-nums" style={{fontFamily: 'Outfit, sans-serif'}}>
                        {k.money ? formatINR(value) : value}
                      </div>
                      <div className="text-xs uppercase tracking-wider text-slate-500 mt-1.5">{k.label}</div>
                    </div>
                  );
                })}
                <div data-testid="manager-kpi-pending_deals_count" className="bg-white border border-slate-200 p-5 rounded-none shadow-sm">
                  <div className="w-9 h-9 bg-slate-100 flex items-center justify-center mb-3">
                    <Handshake size={18} weight="bold" className="text-slate-700" />
                  </div>
                  <div className="text-2xl lg:text-3xl font-bold text-slate-900 tabular-nums" style={{fontFamily: 'Outfit, sans-serif'}}>
                    {data.kpis?.pending_deals_count ?? 0}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mt-1.5">Pending Deals (count)</div>
                </div>
              </div>

              {/* BDE-wise breakdown */}
              <div className="bg-white border border-slate-200 rounded-none shadow-sm mb-8">
                <div className="border-b border-slate-200 px-5 py-3">
                  <h3 className="text-sm uppercase tracking-[0.2em] font-bold text-slate-700">BDE / BDM Breakdown</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[700px]">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">User</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Role</th>
                        <th className="text-right p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Leads</th>
                        <th className="text-right p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Follow-ups</th>
                        <th className="text-right p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Quotations</th>
                        <th className="text-right p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Conversions</th>
                        <th className="text-right p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Missed F/Ups</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.by_bde || []).map((b, i) => (
                        <tr key={b.user_id} data-testid={`manager-bde-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                          <td className="p-3 font-medium text-slate-900">{b.name}</td>
                          <td className="p-3 text-xs uppercase tracking-wider text-slate-600">{b.role}</td>
                          <td className="p-3 text-right tabular-nums">{b.lead_count}</td>
                          <td className="p-3 text-right tabular-nums">{b.follow_up_count}</td>
                          <td className="p-3 text-right tabular-nums">{b.quotation_count}</td>
                          <td className="p-3 text-right tabular-nums text-emerald-700 font-semibold">{b.conversion_count}</td>
                          <td className={`p-3 text-right tabular-nums ${b.missed_followups > 0 ? "text-rose-700 font-semibold" : "text-slate-500"}`}>{b.missed_followups}</td>
                        </tr>
                      ))}
                      {(data.by_bde || []).length === 0 && (
                        <tr><td colSpan={7} className="p-8 text-center text-slate-400 text-sm">No BDE/BDM users found.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Source-wise + Campaign-wise */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <div className="bg-white border border-slate-200 rounded-none shadow-sm">
                  <div className="border-b border-slate-200 px-5 py-3">
                    <h3 className="text-sm uppercase tracking-[0.2em] font-bold text-slate-700">By Source</h3>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Source</th>
                        <th className="text-right p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Leads</th>
                        <th className="text-right p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Conversions</th>
                        <th className="text-right p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Conv. %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.by_source || []).map((s, i) => {
                        const pct = s.lead_count > 0 ? Math.round((s.conversion_count / s.lead_count) * 100) : 0;
                        return (
                          <tr key={s.source} data-testid={`manager-source-row-${i}`} className="border-b border-slate-100">
                            <td className="p-3 text-sm text-slate-700">{s.source}</td>
                            <td className="p-3 text-right tabular-nums">{s.lead_count}</td>
                            <td className="p-3 text-right tabular-nums text-emerald-700">{s.conversion_count}</td>
                            <td className="p-3 text-right tabular-nums">{pct}%</td>
                          </tr>
                        );
                      })}
                      {(data.by_source || []).length === 0 && (
                        <tr><td colSpan={4} className="p-6 text-center text-slate-400 text-sm">No source data.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="bg-white border border-slate-200 rounded-none shadow-sm">
                  <div className="border-b border-slate-200 px-5 py-3">
                    <h3 className="text-sm uppercase tracking-[0.2em] font-bold text-slate-700">By Campaign</h3>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Campaign</th>
                        <th className="text-right p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Leads</th>
                        <th className="text-right p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Conversions</th>
                        <th className="text-right p-3 text-xs uppercase tracking-wider font-bold text-slate-500">Conv. %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.by_campaign || []).map((c, i) => {
                        const pct = c.lead_count > 0 ? Math.round((c.conversion_count / c.lead_count) * 100) : 0;
                        return (
                          <tr key={c.campaign_name} data-testid={`manager-campaign-row-${i}`} className="border-b border-slate-100">
                            <td className="p-3 text-sm text-slate-700">{c.campaign_name}</td>
                            <td className="p-3 text-right tabular-nums">{c.lead_count}</td>
                            <td className="p-3 text-right tabular-nums text-emerald-700">{c.conversion_count}</td>
                            <td className="p-3 text-right tabular-nums">{pct}%</td>
                          </tr>
                        );
                      })}
                      {(data.by_campaign || []).length === 0 && (
                        <tr><td colSpan={4} className="p-6 text-center text-slate-400 text-sm">No campaign attribution yet. Capture social leads to populate this.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManagerDashboard;
